import type { ApplicationFile } from "@/Domain/ApplicationFile";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { useNotificationStore } from "@/stores/useNotificationStore";
import type { UploadFileInfo } from "@progress/kendo-vue-upload";
import { handleResponsePromiseAsync } from "./apiHelper";
import { api } from "@/api";
import { CacheService } from "../services/CacheService";
import { createCacheKey } from "./cacheHelper";
import { useMainStore } from "@/stores/useMainStore";

/* eslint-disable no-control-regex */
export const filenameReservedRegex = () => /[<>:"/\\|?*\u0000-\u001F]/g;

export const filenameReservedRegexWindows = () =>
  /^(con|prn|aux|nul|com\d|lpt\d)$/i;

export const isValidFilename = (str: string): boolean => {
  if (!str || str.length > 255) return false;

  if (str === "." || str === "..") return false;

  return (
    !filenameReservedRegex().test(str) &&
    !filenameReservedRegexWindows().test(str)
  );
};

export const blobToBase64Async = function (blob: Blob): Promise<string | null> {
  const reader = new FileReader();
  reader.readAsDataURL(blob);
  return new Promise((resolve) => {
    reader.onloadend = () => {
      resolve((reader.result as string)?.split(",")[1]);
    };
  });
};

// fallback to 25MB
let MaxTotalUploadSize = 25 * 1024 * 1024;

export const validateTotalUploadSize = function (
  files: UploadFileInfo[],
  showNotification: boolean = true
) {
  const maxTotalUploadSize = MaxTotalUploadSize;
  const totalSize = files.reduce(
    (acc, file) =>
      acc + (file.size ?? (file.getRawFile && file.getRawFile().size) ?? 0),
    0
  );
  const result = totalSize <= maxTotalUploadSize;
  if (!result && showNotification) {
    const notificationStore = useNotificationStore();
    notificationStore.notify({
      message: `Total files size exceeds ${
        maxTotalUploadSize / 1024 / 1024
      } megabytes.`,
      type: NotificationTypeEnum.info,
      duration: 5000,
    });
  }
  return result;
};

export const getAttachmentFilesAsync = async function (
  uploadedFiles: UploadFileInfo[],
  validateRestrictions: boolean = true
) {
  const files: ApplicationFile[] = [];
  if (validateRestrictions)
    uploadedFiles = uploadedFiles.filter((f) => !f.validationErrors);
  for (const doc of uploadedFiles) {
    const rawFile = doc.getRawFile ? doc.getRawFile() : null;
    if (!rawFile) continue;

    const base64 = await blobToBase64Async(rawFile);
    if (!base64) continue;

    files.push({
      Name: doc.name,
      FileBase64String: base64,
    });
  }
  return files;
};

export const getAllowedAttachmentTypesAsync = async function () {
  // fallback to default allowed attachment types
  let allowedAttachmentTypes: string[] = [
    ".pdf",
    ".jpg",
    ".jpeg",
    ".bmp",
    ".tif",
    ".xls",
    ".xlsx",
    ".doc",
    ".docx",
    ".txt",
    ".png",
    ".gif",
    ".msg",
  ];

  await handleResponsePromiseAsync(
    CacheService.getResponseAsync(
      createCacheKey({}, "GetAllowedAttachmentTypes"),
      () => api.get("GetAllowedAttachmentTypes", {})
    ),
    (data) => {
      allowedAttachmentTypes = data;
    },
    "fileHelper.ts",
    "getAllowedAttachmentTypesAsync"
  );

  return allowedAttachmentTypes;
};

export const getMaxTotalUploadSizeAsync = async function () {

  await handleResponsePromiseAsync(
    CacheService.getResponseAsync(
      createCacheKey({}, "GetMaxTotalUploadSize"),
      () => api.get("GetMaxTotalUploadSize", {})
    ),
    (data) => {
      MaxTotalUploadSize = data;
    },
    "fileHelper.ts",
    "getMaxTotalUploadSizeAsync"
  );

  return MaxTotalUploadSize;
};

export default {
  isValidFilename,
  filenameReservedRegex,
  filenameReservedRegexWindows,
  blobToBase64Async,
  getAttachmentFilesAsync,
  getAllowedAttachmentTypesAsync,
};
